import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable/DataTable';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import { toInputDateTime } from '../../utils/dateFormat';
import { exportRowsToExcel } from '../../utils/excel';
import './RadiusReport.css';

const STATUS_OPTIONS = ['SUCCESS', 'FAILED', 'TIMEOUT', 'ERROR', 'PENDING', 'CANCELLED'];

// Today from midnight until now — what the page opens with.
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return toInputDateTime(d); };

const COLUMNS = [
    { label: 'Sr. No.', field: 'sr' },
    { label: 'Username', field: 'username', filter: true },
    { label: 'Status', field: 'status', filter: { options: STATUS_OPTIONS } },
    { label: 'VLAN', field: 'vlan', filter: true },
    { label: 'MAC', field: 'mac', filter: true },
    { label: 'NAS IP', field: 'nasIp', filter: true },
    { label: 'Framed IP', field: 'framedIp', filter: true },
    { label: 'Date', field: 'date', sortable: true },
];

const RadiusReport = () => {
    const { selectedBranch } = useBranchStore();

    const [range, setRange] = useState(() => ({ from: startOfToday(), to: '' }));
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // The date and time picked, as ISO: a bare date sent to the server was read as
    // midnight UTC, so "today to today" found nothing.
    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/auth-logs', {
                params: { branchId: selectedBranch?._id, ...rangeParams(range), limit: 1000 },
            });
            setLogs(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch auth log');
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, range]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const rows = useMemo(() => logs
        .map((log) => ({
            id: log._id,
            username: log.guest?.guestName || log.guest?.roomNumber || log.guest?.mobile || log.guest?.bookingId || '—',
            status: log.result?.authorizationStatus || 'PENDING',
            vlan: log.network?.vlan || '—',
            mac: log.network?.mac || '—',
            nasIp: log.nas?.nasIp || '—',
            framedIp: log.network?.ip || '—',
            date: log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN') : '—',
            createdAt: log.createdAt,
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((row, i) => ({ ...row, sr: i + 1 })),
    [logs]);

    // The table as an Excel file — its Date a real date-time, not the text on screen;
    // the file numbers its rows itself (Sr. No.).
    function handleExportExcel() {
        const columns = COLUMNS.filter((c) => c.field !== 'sr')
            .map((c) => (c.field === 'date' ? { label: c.label, field: 'createdAt' } : c));
        const day = (value) => (value ? String(value).slice(0, 10) : 'now');
        exportRowsToExcel(`radius-report-${day(range.from)}-to-${day(range.to)}`, columns, rows, { sheetName: 'RADIUS Report' });
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-shield-alt"></i>
                            RADIUS Report
                        </h1>
                        <p className="page-subtitle-modern">Auth log — every authentication attempt, success and failure</p>
                    </div>
                </div>
            </div>

            <div className="info-banner-modern">
                <i className="fas fa-info-circle"></i>
                Showing {rows.length} record{rows.length !== 1 ? 's' : ''} for {selectedBranch?.name || 'all branches'}
            </div>

            <div className="radius-filter-bar">
                <DateTimeRange value={range} onChange={setRange} />
                <button type="button" className="btn-primary-modern btn-sm" onClick={fetchLogs}>
                    <i className="fas fa-sync-alt"></i> Refresh
                </button>
            </div>

            <div className="ad-card">
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-list-alt"></i>
                        <h2>Auth results (last 1000)</h2>
                    </div>
                </div>
                <div className="radius-export-bar">
                    <button type="button" className="btn-secondary-modern btn-sm" onClick={handleExportExcel}>
                        <i className="fas fa-file-excel"></i> Excel
                    </button>
                    <button type="button" className="btn-secondary-modern btn-sm" onClick={() => window.print()}>
                        <i className="fas fa-print"></i> Print
                    </button>
                </div>
                <div style={{ padding: '0 1.375rem 1.375rem' }}>
                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div><p>Loading auth log...</p></div>
                    ) : (
                        <DataTable
                            defaultPageSize={100}
                            columns={COLUMNS}
                            rows={rows}
                            rowClassName={(row) => (row.status === 'FAILED' ? 'dt-row-danger' : row.status === 'SUCCESS' ? 'dt-row-success' : '')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default RadiusReport;
